import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import ArrowForward from '@mui/icons-material/ArrowForward'
import CalendarToday from '@mui/icons-material/CalendarToday'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Box,
  Stack,
  Typography,
  Button,
  Skeleton,
  IconButton,
  Popover,
  ListItemButton,
  ButtonBase,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'

import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import type { MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getServiceDateKey,
  getServiceWeekEnd,
  getServiceWeekStart,
  shiftServiceWeek,
} from '../MenuHeader/ServiceSelectionGraphql/serviceWeeks'
import {
  formatDateSelectionDate,
  getDateSelectionWeekStart,
  isDateInSelectionWeek,
} from './dateSelectionDates'
import { getServiceCell } from './serviceStatusDates'
import type { MealKey, ServiceStatus } from './serviceStatusDates'

const POPOVER_MIN_WIDTH = 380
const POPOVER_MAX_HEIGHT = 480
const MEAL_NAME_COLUMN_WIDTH = 80
const SELECTION_BORDER_WIDTH = 3

const canteenDateSelectionUserDataFragment = graphql(`
  fragment CanteenDateSelection_UserDataFragmentV2 on User {
    accountLocation {
      id
      timezone
    }
    upcomingCcMenus {
      date
      lunch {
        meal
        menuIsPublished
        openTime
        closeTime
        orderedItems {
          orderPlaced
        }
      }
      breakfast {
        meal
        menuIsPublished
        openTime
        closeTime
        orderedItems {
          orderPlaced
        }
      }
      dinner {
        meal
        menuIsPublished
        openTime
        closeTime
        orderedItems {
          orderPlaced
        }
      }
    }
  }
`)

interface DateSelectionCardProps {
  userData: FragmentType<typeof canteenDateSelectionUserDataFragment>
  meal: string
  date: string
  onChangeSelectedService: (input: { meal: string; date: string }) => void
}

const MEAL_KEYS: readonly MealKey[] = ['breakfast', 'lunch', 'dinner'] as const

const getStatusPalette = (theme: Theme): Record<ServiceStatus, { bg: string; fg: string }> => ({
  open: {
    bg: alpha(theme.palette.success.main, 0.16),
    fg: theme.palette.success.dark,
  },
  placed: {
    bg: alpha(theme.palette.info.main, 0.3),
    fg: theme.palette.info.contrastText ?? theme.palette.text.primary,
  },
  closed: {
    bg: theme.palette.action.disabledBackground,
    fg: theme.palette.text.secondary,
  },
  noMenu: {
    bg: alpha(theme.palette.warning.main, 0.12),
    fg: theme.palette.warning.main,
  },
  openInFuture: {
    bg: alpha(theme.palette.info.main, 0.12),
    fg: theme.palette.info.main,
  },
})

interface StatusChipProps {
  status: ServiceStatus
  label: string
}
function StatusChip({ status, label }: StatusChipProps) {
  const theme = useTheme()
  const { bg, fg } = getStatusPalette(theme)[status]
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 500,
        px: 1,
        py: '2px',
        borderRadius: `${theme.shape.borderRadiusChip}px`,
        bgcolor: bg,
        color: fg,
      }}>
      {label}
    </Box>
  )
}

export function DateSelectionCard({
  userData,
  meal,
  date,
  onChangeSelectedService,
}: DateSelectionCardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { accountLocation, upcomingCcMenus } = useFragment(
    canteenDateSelectionUserDataFragment,
    userData,
  )
  const timezone = accountLocation?.timezone

  const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
    getDateSelectionWeekStart(timezone),
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const STATUS_LABEL: Record<ServiceStatus, string> = {
    open: t('cc:navBar.serviceStatus.ordersOpen'),
    placed: t('cc:navBar.serviceStatus.orderPlaced'),
    closed: t('cc:navBar.serviceStatus.closed'),
    noMenu: t('cc:navBar.serviceStatus.menuNotReady'),
    openInFuture: t('cc:navBar.serviceStatus.openInFuture'),
  }

  const getStatusLabel = (status: ServiceStatus, timeUntilMenuOpen?: string | null) => {
    if (status === 'openInFuture' && timeUntilMenuOpen) {
      return t('cc:navBar.serviceStatus.openInFuture', { time: timeUntilMenuOpen })
    }
    return STATUS_LABEL[status]
  }

  const MEAL_LABEL: Record<MealKey, string> = {
    breakfast: t('cc:navBar.breakfast'),
    lunch: t('cc:navBar.lunch'),
    dinner: t('cc:navBar.dinner'),
  }

  const dayRows = useMemo(() => {
    if (!timezone || !upcomingCcMenus?.length) return []
    const todayStr = getServiceDateKey(new Date(), timezone)
    return upcomingCcMenus
      .filter((d) => isDateInSelectionWeek(d.date, selectedWeekStart, timezone))
      .map((d) => {
        const dayDateStr = getServiceDateKey(d.date, timezone) ?? 'Invalid date'
        const meals = MEAL_KEYS.map((mealKey) => ({
          mealKey,
          ...getServiceCell(d[mealKey], mealKey, new Date(), timezone),
        })).filter((m) => m.status !== null)
        return {
          date: d.date,
          dayDateStr,
          isToday: dayDateStr === todayStr,
          meals,
          allDisabled: meals.every((m) => m.isDisabled),
        }
      })
      .filter((day) => day.meals.length > 0)
  }, [upcomingCcMenus, selectedWeekStart, timezone])

  const { isAtEarliestWeek, isAtLatestWeek } = useMemo(() => {
    if (!timezone || !upcomingCcMenus?.length) {
      return { isAtEarliestWeek: true, isAtLatestWeek: true }
    }
    const earliest = getServiceWeekStart(upcomingCcMenus[0].date, timezone)
    const latest = getServiceWeekEnd(upcomingCcMenus[upcomingCcMenus.length - 1].date, timezone)
    return {
      isAtEarliestWeek: isBefore(shiftServiceWeek(selectedWeekStart, -1, timezone), earliest),
      isAtLatestWeek: isAfter(shiftServiceWeek(selectedWeekStart, 1, timezone), latest),
    }
  }, [upcomingCcMenus, selectedWeekStart, timezone])

  const selectedDateStr = date

  const orderedServices = useMemo(() => {
    if (!timezone || !upcomingCcMenus?.length) return []
    return upcomingCcMenus.flatMap((d) => {
      const dayDateStr = getServiceDateKey(d.date, timezone) ?? 'Invalid date'
      return MEAL_KEYS.map((mealKey) => {
        const { isDisabled } = getServiceCell(d[mealKey], mealKey, new Date(), timezone)
        return { date: d.date, dayDateStr, mealKey, isDisabled }
      })
    })
  }, [upcomingCcMenus, timezone])

  const nextService = useMemo(() => {
    if (!orderedServices.length) return null
    const currentIndex = orderedServices.findIndex(
      (s) => s.dayDateStr === date && s.mealKey === meal,
    )
    const searchFrom = currentIndex === -1 ? 0 : currentIndex + 1
    return orderedServices.slice(searchFrom).find((s) => !s.isDisabled) ?? null
  }, [orderedServices, date, meal])

  const handleOpenDateSelection = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
  const handleCloseDateSelection = () => setAnchorEl(null)

  const handlePreviousWeekSelect = () => {
    if (!timezone || !upcomingCcMenus?.length) return
    const earliestWeekStartInData = getServiceWeekStart(upcomingCcMenus[0].date, timezone)
    setSelectedWeekStart((prev) => {
      const previousWeekStart = shiftServiceWeek(prev, -1, timezone)
      if (isBefore(previousWeekStart, earliestWeekStartInData)) return prev
      return previousWeekStart
    })
  }

  const handleNextWeekSelect = () => {
    if (!timezone || !upcomingCcMenus?.length) return
    const latestWeekEndInData = getServiceWeekEnd(
      upcomingCcMenus[upcomingCcMenus.length - 1].date,
      timezone,
    )
    setSelectedWeekStart((prev) => {
      const nextWeekStart = shiftServiceWeek(prev, 1, timezone)
      if (isAfter(nextWeekStart, latestWeekEndInData)) return prev
      return nextWeekStart
    })
  }

  if (!timezone) {
    return (
      <Button
        fullWidth
        variant="outlined"
        disabled
        sx={{
          display: 'block',
          borderColor: 'divider',
          minHeight: 40,
          boxShadow: 'none',
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton width="100%" />
          <ExpandMore color="action" />
        </Stack>
      </Button>
    )
  }

  const handleSelectMeal = (day: string, mealKey: MealKey) => {
    onChangeSelectedService({
      date: getServiceDateKey(day, timezone) ?? 'Invalid date',
      meal: mealKey,
    })
    handleCloseDateSelection()
  }

  const handleSelectNextMeal = () => {
    if (!nextService) return
    onChangeSelectedService({
      date: getServiceDateKey(nextService.date, timezone) ?? 'Invalid date',
      meal: nextService.mealKey,
    })
  }

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ width: '100%' }}>
        <ButtonBase
          onClick={handleOpenDateSelection}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            gap: 0.75,
            px: 0.5,
            py: 0.5,
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'primary.main',
            transition: theme.transitions.create('border-color'),
            '&:hover': { borderColor: 'primary.dark' },
          }}>
          <CalendarToday sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 400,
              color: 'primary.main',
              whiteSpace: 'nowrap',
            }}>
            {date
              ? formatDateSelectionDate(
                  date,
                  timezone,
                  isMobile ? 'EEE do MMM' : 'EEEE do MMMM yyyy',
                )
              : ''}
          </Typography>
          {open ? (
            <ExpandLess sx={{ fontSize: 20, color: 'primary.main' }} />
          ) : (
            <ExpandMore sx={{ fontSize: 20, color: 'primary.main' }} />
          )}
        </ButtonBase>

        {isMobile ? (
          <IconButton
            size="small"
            onClick={handleSelectNextMeal}
            disabled={!nextService}
            aria-label={t('cc:navBar.nextMeal')}
            sx={{
              border: '1px solid',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}>
            <ArrowForward fontSize="small" />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowForward />}
            onClick={handleSelectNextMeal}
            disabled={!nextService}
            sx={{ whiteSpace: 'nowrap', ml: 'auto' }}>
            {t('cc:navBar.nextMeal')}
          </Button>
        )}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseDateSelection}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: POPOVER_MIN_WIDTH,
              maxHeight: POPOVER_MAX_HEIGHT,
              overflowY: 'auto',
              mt: 0.5,
            },
          },
        }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            py: 1,
          }}>
          <IconButton
            size="small"
            onClick={handlePreviousWeekSelect}
            disabled={isAtEarliestWeek}
            aria-label={t('cc:navBar.previousWeek')}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            {formatDateSelectionDate(selectedWeekStart, timezone, 'do MMM')} -{' '}
            {formatDateSelectionDate(
              getServiceWeekEnd(selectedWeekStart, timezone),
              timezone,
              'do MMM',
            )}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextWeekSelect}
            disabled={isAtLatestWeek}
            aria-label={t('cc:navBar.nextWeek')}>
            <ChevronRight />
          </IconButton>
        </Stack>

        {dayRows.map((day, idx) => (
          <Box
            key={day.date}
            sx={{
              borderTop: idx === 0 ? 'none' : '1px solid',
              borderTopColor: 'divider',
            }}>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{
                px: 2,
                pt: 1.25,
                pb: 0.625,
                bgcolor: 'background.default',
              }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: day.allDisabled ? 'text.disabled' : 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                {formatDateSelectionDate(day.date, timezone, 'EEEE do MMM')}
              </Typography>
              {day.isToday && (
                <Box
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '3px',
                    px: 0.625,
                    py: '1px',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                  }}>
                  {t('cc:navBar.today')}
                </Box>
              )}
            </Stack>

            {day.meals.map(({ mealKey, status, isDisabled, timeUntilMenuOpen }) => {
              const isSelected = day.dayDateStr === selectedDateStr && mealKey === meal
              return (
                <ListItemButton
                  key={mealKey}
                  selected={isSelected}
                  disabled={isDisabled}
                  onClick={() => handleSelectMeal(day.date, mealKey)}
                  sx={{
                    py: 1,
                    pl: 3.5,
                    pr: 2,
                    gap: 1.5,
                    borderLeft: `${SELECTION_BORDER_WIDTH}px solid transparent`,
                    transition: theme.transitions.create(['background-color', 'border-color'], {
                      duration: theme.transitions.duration.shortest,
                    }),
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderLeftColor: 'primary.main',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                    },
                    '&.Mui-disabled': {
                      opacity: 1,
                    },
                  }}>
                  <Typography
                    sx={{
                      width: MEAL_NAME_COLUMN_WIDTH,
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 500,
                      flexShrink: 0,
                      color: isSelected
                        ? 'primary.main'
                        : isDisabled
                          ? 'text.disabled'
                          : 'text.primary',
                    }}>
                    {MEAL_LABEL[mealKey]}
                  </Typography>
                  {status && (
                    <StatusChip status={status} label={getStatusLabel(status, timeUntilMenuOpen)} />
                  )}
                </ListItemButton>
              )
            })}
          </Box>
        ))}
      </Popover>
    </>
  )
}
